import EventSystem from './utils/EventSystem';
import { DepthFirstNodeDependencyMap, OrderNodes } from './utils/Graph';

class Lines {
  constructor(args) {
    this.width = document.body.clientWidth;
    this.height = document.body.clientHeight;

    this.inPercent = 0.85;
    this.innerRadius = Math.min(this.width, this.height) * 0.4 * this.inPercent;
    this.outerRadius = Math.min(this.width, this.height) * 0.5 * this.inPercent;
    this.colors = ['#1f6ada', '#ff3900', '#00cc99'];
    this.hidden = true;

    this.container = d3.select('.lines')
      .style('display', 'none')
      ;
    this.svg = this.container
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .append('g')
      .attr('id', 'circle')
      .attr('transform', `translate(${this.width / 2}, ${this.height / 2})`)
      ;
    this.bigCircle = this.svg.append('circle')
      .attr('id', 'big-circle')
      .attr('r', this.innerRadius * 0.979)
      ;
    this.svg.append('rect')
      .attr('width', '300')
      .attr('height', '50')
      .attr('x', '-150')
      .attr('y', '-34%')
      .attr('id', 'hider-hack')
      ;
    this.svg.append('circle')
      .attr('id', 'lines-low-level-circle')
      .attr('cx', -150)
      .attr('cy', '-27.7%')
      .attr('r', 10)
      ;
    this.svg.append('circle')
      .attr('id', 'lines-high-level-circle')
      .attr('cx', 150)
      .attr('cy', '-27.7%')
      .attr('r', 10)
      ;
    this.label1 = this.svg.append('text')
      .attr('fill', '#333')
      .attr('x', -175)
      .attr('y', '-32%')
      .text('Nível 10')
      ;
    this.label2 = this.svg.append('text')
      .attr('fill', '#333')
      .attr('x', 125)
      .attr('y', '-32%')
      .text('Nível 1')
      ;

    this.link = this.svg.append('g').attr('id', 'link-group-container');
    this.node = this.svg.append('g').attr('id', 'node-group-container');
    this.label = this.container.append('div')
      .attr('id', 'lines-labels')
      .style('transform', `translate(${this.width / 2}px, ${this.height / 2}px)`)
    ;

    this.commonLabelContainer = d3.select('div.labels');

    this.cluster = d3.cluster()
      .size([360, this.innerRadius]);

    this.line = d3.radialLine()
      .curve(d3.curveBundle.beta(0.85))
      .radius(d => d.radius)
      .angle(d => (d.angle + 90) / 180 * Math.PI);

    EventSystem.$on('selected-node', node => {
      if (this.hidden) return;
      this.nodeClick(node);
    });
    EventSystem.$on('tree-view-close', () => {
      this.treeViewClose();
    });
  }
  updateView(nodes) {
    this.links = window.$links.map(l => ({
      ...l,
      source: nodes.find(n => n.id === l.source),
      target: nodes.find(n => n.id === l.target),
    })).filter(l => l.source && l.target);

    const map = DepthFirstNodeDependencyMap(nodes, this.links);

    this.nodes = map.nodeLine;

    let nodeLength = Math.round(this.nodes.length * 1.2);
    if (this.nodes.length % 2 === nodeLength % 2) {
      nodeLength += 1;
    }
    const angleStep = 360 / nodeLength;
    const halfLength = this.nodes.length * 0.5;
    const pad = Math.round((nodeLength - this.nodes.length) / 2) * angleStep;
    this.halfLength = halfLength;
    this.nodeLength = nodeLength;
    this.angleStep = angleStep;
    this.pad = pad;

    this.nodes.forEach((node, i) => {
      const angle = (i * angleStep) - 90 + pad;
      const distance = this.innerRadius;
      node.x = Math.cos((angle / 180) * Math.PI) * distance;
      node.y = Math.sin((angle / 180) * Math.PI) * distance;
      node.angle = angle;
      node.radius = distance * 0.95;
      node.r = Math.min(25, 1000 / nodeLength);
    });

    const linkDataMerge = this.link
      .selectAll('.link')
      .data(this.links);
    this.updateLinks(linkDataMerge);
    this.updateLinks(linkDataMerge.enter().append('path'));
    linkDataMerge.exit().remove();

    const nodeDataMerge = this.node
      .selectAll('g')
      .data(this.nodes);
    this.updateNodes(nodeDataMerge, false);
    this.updateNodes(nodeDataMerge.enter().append('g'), true);
    nodeDataMerge.exit().remove();

    const labelDataMerge = this.label
      .selectAll('.lines-label')
      .data(this.nodes);
    this.updateLabels(labelDataMerge, false);
    this.updateLabels(labelDataMerge.enter().append('div'), true);
    labelDataMerge.exit().remove();

    this.label1.text(`Nível ${map.highestLevel}`);
    this.label2.text(`Nível ${map.lowestLevel}`);
  }
  animateIn() {
    this.container
      .style('opacity', 0)
      .style('display', 'block')
      .transition()
        .duration(150)
        .style('opacity', 1)
      ;

    this.link
      .selectAll('.link')
      .style('opacity', 0)
      .transition()
        .delay((d, i, a) => Math.floor(i * (700 / a.length)) + 250)
        .duration(350)
        .ease(d3.easeSin)
        .style('opacity', 1)
      ;

    this.node
      .selectAll('g')
      .transition()
        .duration(500)
        .ease(d3.easeSin)
        .attr('transform', (d, i) => `rotate(${(i * this.angleStep) - 90 + this.pad}) translate(${this.innerRadius * 1}, 0) rotate(${i > this.halfLength ? 180 : 0})`)
      ;

    this.svg
      .attr('transform', `translate(${this.width / 2}, ${this.height / 2}) scale(0)`)
      .transition()
        .duration(500)
        .ease(d3.easeSin)
        .attr('transform', `translate(${this.width / 2}, ${this.height / 2}) scale(1)`)
  }
  animateOut() {
    this.link
      .selectAll('.link')
      .style('opacity', 1)
      .transition()
        .delay((d, i, a) => Math.floor(i * (300 / a.length)))
        .duration(350)
        .ease(d3.easeSin)
        .style('opacity', 0)
      ;

    this.node
      .selectAll('g')
      .transition()
        .delay(300)
        .duration(500)
        .ease(d3.easeSin)
        .attr('transform', (d, i) => `rotate(${(i * this.angleStep) - 90 + this.pad}) translate(0, 0)`)
      ;

    this.svg
      .attr('transform', `translate(${this.width / 2}, ${this.height / 2}) scale(1)`)
      .transition()
        .delay(300)
        .duration(500)
        .ease(d3.easeSin)
        .attr('transform', `translate(${this.width / 2}, ${this.height / 2}) scale(0)`)
        .on('end', () => {
          this.container
            .transition()
              .duration(150)
              .style('opacity', 0)
              .on('end', () => {
                this.container.style('display', 'none')
              })
          ;
        });
  }
  updateLinks(selection) {
    selection
      .attr('class', 'link')
      .attr('stroke', d => (d.source ? this.colors[d.source.group] : ''))
      .attr('d', d => {
        if (d.source && d.target) {
          return this.line([d.source, { angle: 0, radius: 0 }, d.target]);
        }
        return null;
      })
    ;
  }
  updateNodes(selection, isNew) {
    selection
      .attr('class', 'line-nodes')
      .attr('transform', (d, i) => `rotate(${(i * this.angleStep) - 90 + this.pad})`)
      ;

    let circles;

    if (isNew) {
      circles = selection.append('circle');
    } else {
      circles = this.node.selectAll('circle').data(this.nodes);
    }

    circles
      .attr('cx', (d, i) => (i > this.halfLength ? 7 : -7))
      .attr('cy', 0)
      .attr('r', d => d.r)
      .attr('fill', d => this.colors[d.group])
      .attr('id', d => `node-${d.id}`)
      .style('opacity', d => 1)
      .style('transform', '')
      .on('mouseover', node => this.nodeMouseOver(node))
      .on('mouseout', node => this.nodeMouseOut(node))
      .on('click', node => this.nodeClick(node))
    ;
  }
  updateLabels(selection, isNew) {
    selection
      .attr('class', 'lines-label')
      .attr('id', d => `lines-label-${d.id}`)
      .style('left', d => d.x >= 0 ? `${d.x}px` : null)
      .style('right', d => d.x < 0 ? `${-d.x}px` : null)
      .style('top', d => `${d.y}px`)
      .style('display', 'none')
      .on('mouseover', node => this.nodeMouseOver(node))
      .on('mouseout', node => this.nodeMouseOut(node))
      .on('click', node => this.nodeClick(node))
    ;

    let labels;
    if (isNew) {
      labels = selection.append('div');
    } else {
      labels = selection.select('div');
    }

    labels
      .attr('class', 'labels-in')
      .text(d => d.cargo)
    ;
  }
  nodeMouseOver(node) {
    d3.select(`#lines-label-${node.id}`)
      .style('display', 'block')
    ;
  }
  nodeMouseOut(node) {
    d3.select(`#lines-label-${node.id}`)
      .style('display', 'none')
    ;
  }
  nodeClick(node) {
    if (this.selectedNode) {
      this.selectedNode.selected = false;
    }

    this.selectedNode = this.nodes.find(n => n.id === node.id)
    this.selectedNode.selected = true;
    EventSystem.$emit('tree-view-open', this.selectedNode);

    this.treeViewOpen();

    this.node
      .data(this.nodes)
      .selectAll('circle')
      .attr('r', d => d.selected ? d.r * 1.5 : d.r / 1.2)
      .style('opacity', d => d.selected ? 1 : 0.7)
      .style('transform', (d, i) => {
        if (!d.selected) return '';
        if (i <= this.halfLength) {
          return `translate(${d.r * 2}px, 0)`;
        }
        return `translate(${d.r * -2}px, 0)`;
      })
      ;
  }
  hide() {
    this.animateOut();
    this.commonLabelContainer.style('display', null);
    this.hidden = true;
  }
  show() {
    this.animateIn();
    this.commonLabelContainer.style('display', 'none');
    this.hidden = false;
  }
  treeViewOpen() {
    this.svg
      .attr('transform', `translate(${((this.width - 361) / 2) + 361}, ${this.height / 2})`)
      ;

    this.label
      .style('transform', `translate(${((this.width - 361) / 2) + 361}px, ${this.height / 2}px)`)
      ;
  }
  treeViewClose() {
    this.svg
      .attr('transform', `translate(${this.width / 2}, ${this.height / 2})`)
      ;

    this.label
      .style('transform', `translate(${this.width / 2}px, ${this.height / 2}px)`)
      ;
  }
};

export default Lines;



// WEBPACK FOOTER //
// ./assets/js/source/lines.js